/**
 * 主动缓存工具类
 * 功能：
 *  1) 加载 file.json 获取依赖文件列表
 *  2) 主动缓存所有依赖文件到对应的缓存桶
 *  3) 缓存策略与 sw.js 保持一致
 *  4) 检查文件大小，避免缓存 0KB 文件
 */

export interface ProactiveCacheOptions {
  staticCacheName?: string;
  htmlCacheName?: string;
  jsonCacheName?: string;
  fileJsonPath?: string;
  onProgress?: (current: number, total: number, fileName?: string) => void;
  onError?: (error: Error, fileName?: string) => void;
}

export interface CacheResult {
  success: number;
  failed: number;
  skipped: number;
  total: number;
  errors: Array<{ file: string; error: Error }>;
}

export class ProactiveCacheHelper {
  private staticCache: string;
  private htmlCache: string;
  private jsonCache: string;
  private fileJsonPath: string;
  private onProgress?: (current: number, total: number, fileName?: string) => void;
  private onError?: (error: Error, fileName?: string) => void;

  // HTML 文件列表（不在 file.json 中）
  private static HTML_FILES = ["/", "/webclip.html", "/restrict.html"];

  // 相对地址白名单列表 - 这些资源缓存时保存相对地址
  private static RELATIVE_PATH_WHITELIST = [
    "/images/restrict/new-domain.png",
    "/images/restrict/kefu.png",
    "/images/restrict/qq.png",
    "/images/restrict/mail.png",
    "/images/restrict/bg-restrict.js",
    "/js/CacheFileStore.js",
    "/js/DomainPoller.js",
    "/config.js"
  ];

  constructor(opts: ProactiveCacheOptions = {}) {
    this.staticCache = opts.staticCacheName || "static-cache";
    this.htmlCache = opts.htmlCacheName || "html-cache";
    this.jsonCache = opts.jsonCacheName || "json-cache";
    this.fileJsonPath = opts.fileJsonPath || "/file.json";
    this.onProgress = opts.onProgress;
    this.onError = opts.onError;
  }

  /**
   * 检查资源是否在相对路径白名单中
   */
  private isInRelativePathWhitelist(url: string): boolean {
    try {
      const urlObj = new URL(url, location.href);
      const pathname = urlObj.pathname;
      return ProactiveCacheHelper.RELATIVE_PATH_WHITELIST.includes(pathname);
    } catch {
      return false;
    }
  }

  /**
   * 根据文件扩展名确定缓存桶
   */
  private pickCacheName(urlStr: string): string {
    const url = new URL(urlStr, location.href);
    let pathname = url.pathname.toLowerCase();

    // 处理 //oss 开头的路径
    if (pathname.startsWith("//")) {
      pathname = pathname.substring(1);
    }

    // /oss/ 路径下的文件都归入 image-cache
    if (pathname.startsWith("/oss/")) {
      return "image-cache";
    }

    // JSON 文件
    if (pathname.endsWith(".json")) {
      // 以 /_nuxt/ 开头的是静态资源 JSON，否则是数据 JSON
      return pathname.startsWith("/_nuxt/") ? this.staticCache : this.jsonCache;
    }

    // JS/CSS 文件
    if (/\.(js|mjs|css)$/i.test(pathname)) {
      return this.staticCache;
    }

    // HTML 文件
    if (pathname.endsWith(".html") || pathname === "/") {
      return this.htmlCache;
    }

    // 图片和其他静态资源
    if (/\.(jpg|jpeg|png|bmp|gif|svg|webp|ico|webm|mp3)$/i.test(pathname)) {
      return "image-cache";
    }

    // 默认
    return this.staticCache;
  }

  /**
   * 检查缓存中是否已有该文件且大小不为 0
   */
  private async checkCacheStatus(url: string): Promise<{ cached: boolean; size: number }> {
    try {
      const cacheName = this.pickCacheName(url);
      const cache = await caches.open(cacheName);

      // 根据白名单决定查找的URL
      const searchUrl = this.isInRelativePathWhitelist(url) ? new URL(url, location.href).pathname : url;
      const response = await cache.match(new Request(searchUrl), { ignoreSearch: false });

      if (!response) {
        return { cached: false, size: 0 };
      }

      // 对于 opaque 响应，我们无法获取大小，但仍然认为它已缓存
      if (response.type === "opaque") {
        return { cached: true, size: 1 }; // 返回非零值表示已缓存
      }

      // 检查文件大小
      const contentLength = response.headers.get("content-length");
      const size = contentLength ? parseInt(contentLength, 10) : 0;

      // 如果没有 content-length，尝试读取 body
      if (size === 0 && response.body) {
        try {
          const blob = await response.blob();
          return { cached: true, size: blob.size };
        } catch {
          return { cached: true, size: 0 };
        }
      }

      return { cached: true, size };
    } catch {
      return { cached: false, size: 0 };
    }
  }

  /**
   * 缓存单个文件
   */
  private async cacheFile(url: string): Promise<boolean> {
    try {
      // 检查是否已缓存且大小不为 0
      const { cached, size } = await this.checkCacheStatus(url);
      if (cached && size > 0) {
        return true; // 跳过已缓存的文件
      }

      // 获取缓存桶
      const cacheName = this.pickCacheName(url);
      const cache = await caches.open(cacheName);

      // 判断是否是跨域资源
      const urlObj = new URL(url);
      const isCrossOrigin = urlObj.origin !== location.origin;

      // 构建请求选项
      // const fetchOptions: RequestInit = {
      //   method: "GET",
      //   mode: isCrossOrigin ? "cors" : "same-origin",
      //   credentials: isCrossOrigin ? "omit" : "same-origin",
      //   cache: "no-cache",
      // };

      // 添加参数强制绕过 Service Worker
      const bypassUrl = url + (url.includes("?") ? "&" : "?") + "_bypass_sw=" + Date.now();

      const iosFetchOptions: RequestInit = {
        method: "GET",
        mode: isCrossOrigin ? "cors" : "same-origin",
        credentials: "omit",
        cache: "no-store",
      };

      try {
        const response = await fetch(bypassUrl, iosFetchOptions);
        console.log("Caching (iOS):", url, "->", cacheName);

        // 检查响应
        if (!response.ok && response.type !== "opaque") {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // 根据白名单决定缓存地址
        const cacheUrl = this.isInRelativePathWhitelist(url) ? new URL(url, location.href).pathname : url;
        await cache.put(new Request(cacheUrl), response.clone());

        return true;
      } catch (error) {
        // 如果 CORS 请求失败，尝试 no-cors
        if (isCrossOrigin) {
          console.warn("CORS request failed, trying no-cors:", error);

          const noCorsResponse = await fetch(bypassUrl, {
            method: "GET",
            mode: "no-cors",
            cache: "no-store",
          });

          console.log("Caching (no-cors):", url, "->", cacheName);

          // 对于 no-cors 请求，直接缓存 opaque 响应
          const cacheUrl = this.isInRelativePathWhitelist(url) ? new URL(url, location.href).pathname : url;
          await cache.put(new Request(cacheUrl), noCorsResponse.clone());

          return true;
        }
        throw error;
      }

      // 对于 iOS Safari，添加参数绕过 Service Worker
      // if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      //
      // } else {
      //   // 非 iOS 设备的处理
      //   const response = await fetch(url, fetchOptions);
      //   console.log("Caching:", url, "->", cacheName);
      //   console.log("Response:", response);
      //
      //   // 检查响应是否有效
      //   if (!response.ok && response.type !== "opaque") {
      //     throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      //   }
      //
      //   // 对于 opaque 响应（跨域无 CORS），直接缓存
      //   if (response.type === "opaque") {
      //     const cacheUrl = this.isInRelativePathWhitelist(url) ? new URL(url, location.href).pathname : url;
      //     await cache.put(new Request(cacheUrl), response.clone());
      //     return true;
      //   }
      //
      //   // 检查响应大小
      //   const responseSize = parseInt(response.headers.get("content-length") || "0", 10);
      //   if (responseSize === 0) {
      //     // 尝试克隆并读取 body 来确认大小
      //     const cloned = response.clone();
      //     const blob = await cloned.blob();
      //     if (blob.size === 0) {
      //       throw new Error("Response body is empty");
      //     }
      //   }
      //
      //   // 缓存响应
      //   const cacheUrl = this.isInRelativePathWhitelist(url) ? new URL(url, location.href).pathname : url;
      //   await cache.put(new Request(cacheUrl), response.clone());
      //   return true;
      // }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      if (this.onError) {
        this.onError(err, url);
      }
      throw err;
    }
  }

  /**
   * 加载 file.json
   */
  private async loadFileJson(): Promise<Record<string, string>> {
    try {
      // file.json 也需要使用资源域名
      const fileJsonUrl = this.getResourceUrl(this.fileJsonPath);
      console.log("Loading file.json from:", fileJsonUrl);

      // 对 iOS 设备特殊处理
      let response: Response;
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        // iOS 上添加绕过参数
        const bypassUrl = fileJsonUrl + (fileJsonUrl.includes("?") ? "&" : "?") + "_bypass_sw=" + Date.now();
        response = await fetch(bypassUrl, {
          method: "GET",
          mode: "cors",
          credentials: "omit",
          cache: "no-store",
        });
      } else {
        response = await fetch(fileJsonUrl);
      }

      if (!response.ok) {
        throw new Error(`Failed to load file.json: HTTP ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log("file.json loaded successfully, files count:", Object.keys(data).length);
      return data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      console.error("Failed to load file.json:", err);
      if (this.onError) {
        this.onError(err, this.fileJsonPath);
      }
      throw err;
    }
  }

  /**
   * 缓存 HTML 文件
   */
  private async cacheHtmlFiles(): Promise<CacheResult> {
    const result: CacheResult = {
      success: 0,
      failed: 0,
      skipped: 0,
      total: ProactiveCacheHelper.HTML_FILES.length,
      errors: [],
    };

    for (let i = 0; i < ProactiveCacheHelper.HTML_FILES.length; i++) {
      const file = ProactiveCacheHelper.HTML_FILES[i];
      // HTML 文件始终使用主域名，构建完整 URL
      const htmlUrl = new URL(file, location.href).href;

      try {
        const { cached, size } = await this.checkCacheStatus(htmlUrl);
        if (cached && size > 0) {
          result.skipped++;
        } else {
          await this.cacheFile(htmlUrl);
          result.success++;
        }
      } catch (error) {
        result.failed++;
        result.errors.push({
          file: htmlUrl,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      }

      if (this.onProgress) {
        this.onProgress(i + 1, result.total, file);
      }
    }

    return result;
  }

  /**
   * 获取资源的完整 URL
   */
  private getResourceUrl(file: string): string {
    // HTML 文件和根路径始终使用主域名
    if (file === "/" || file.endsWith(".html")) {
      return new URL(file, location.href).href;
    }

    // 检查是否需要使用资源域名
    // const isResourceFile = /\.(js|mjs|css|json)$/i.test(file);
    // 读取 data-custom 属性， 这个是后端模板渲染时注入的资源域名
    const domAssertApi = document.body.getAttribute("data-custom") || "";
    if (domAssertApi) {
      // 使用资源域名
      const baseUrl = domAssertApi;
      // 确保 baseUrl 以 / 结尾，file 不以 / 开头
      const normalizedBase = baseUrl.endsWith("/") ? baseUrl : baseUrl + "/";
      const normalizedFile = file.startsWith("/") ? file.slice(1) : file;
      return normalizedBase + normalizedFile;
    }

    // 默认使用当前域名
    return new URL(file, location.href).href;
  }

  /**
   * 缓存所有依赖文件
   */
  async cacheAllDependencies(): Promise<CacheResult> {
    const totalResult: CacheResult = {
      success: 0,
      failed: 0,
      skipped: 0,
      total: 0,
      errors: [],
    };

    // 1. 缓存 HTML 文件
    const htmlResult = await this.cacheHtmlFiles();
    totalResult.success += htmlResult.success;
    totalResult.failed += htmlResult.failed;
    totalResult.skipped += htmlResult.skipped;
    totalResult.total += htmlResult.total;
    totalResult.errors.push(...htmlResult.errors);

    // 2. 加载 file.json
    let fileMap: Record<string, string>;
    try {
      fileMap = await this.loadFileJson();
    } catch (error) {
      // file.json 加载失败，返回当前结果
      return totalResult;
    }

    // 3. 缓存 file.json 中的文件
    const files = Object.values(fileMap);
    const startIndex = totalResult.total;
    totalResult.total += files.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fullUrl = this.getResourceUrl(file);

      try {
        const { cached, size } = await this.checkCacheStatus(fullUrl);
        if (cached && size > 0) {
          totalResult.skipped++;
        } else {
          await this.cacheFile(fullUrl);
          totalResult.success++;
        }
      } catch (error) {
        totalResult.failed++;
        totalResult.errors.push({
          file: fullUrl,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      }

      if (this.onProgress) {
        this.onProgress(startIndex + i + 1, totalResult.total, file);
      }
    }

    return totalResult;
  }

  /**
   * 获取缓存统计信息
   */
  async getCacheStats(): Promise<{
    staticCache: { count: number; urls: string[] };
    htmlCache: { count: number; urls: string[] };
    jsonCache: { count: number; urls: string[] };
  }> {
    const getStats = async (cacheName: string) => {
      try {
        const cache = await caches.open(cacheName);
        const keys = await cache.keys();
        return {
          count: keys.length,
          urls: keys.map(req => req.url),
        };
      } catch {
        return { count: 0, urls: [] };
      }
    };

    const [staticStats, htmlStats, jsonStats] = await Promise.all([getStats(this.staticCache), getStats(this.htmlCache), getStats(this.jsonCache)]);

    return {
      staticCache: staticStats,
      htmlCache: htmlStats,
      jsonCache: jsonStats,
    };
  }

  /**
   * 清理指定缓存桶
   */
  async clearCache(cacheName?: string): Promise<boolean> {
    try {
      if (cacheName) {
        return await caches.delete(cacheName);
      } else {
        // 清理所有相关缓存
        const results = await Promise.all([caches.delete(this.staticCache), caches.delete(this.htmlCache), caches.delete(this.jsonCache)]);
        return results.some(r => r);
      }
    } catch {
      return false;
    }
  }
}

// 导出便捷方法
export async function proactiveCacheAll(options?: ProactiveCacheOptions): Promise<CacheResult> {
  const helper = new ProactiveCacheHelper(options);
  return helper.cacheAllDependencies();
}

/**
 *   使用示例：
 *   import { ProactiveCacheHelper, proactiveCacheAll } from '~/utils/ProactiveCacheHelper'
 *
 *   // 方法1：使用便捷函数
 *   const result = await proactiveCacheAll({
 *     onProgress: (current, total, fileName) => {
 *       console.log(`缓存进度: ${current}/${total} - ${fileName}`)
 *     },
 *     onError: (error, fileName) => {
 *       console.error(`缓存失败: ${fileName}`, error)
 *     }
 *   })
 *
 *   // 方法2：使用类实例
 *   const helper = new ProactiveCacheHelper({
 *     staticCacheName: 'static-cache',
 *     htmlCacheName: 'html-cache',
 *     jsonCacheName: 'json-cache',
 *     fileJsonPath: '/file.json'
 *   })
 *
 *   const result = await helper.cacheAllDependencies()
 *   console.log(`缓存完成: 成功${result.success}个，失败${result.failed}个，跳过${result.skipped}个`)
 */
